import React from "react";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Lightbulb, MessageSquare, Save, Send } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

export const ResearchPanel = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [chatMessages, setChatMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [input, setInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isChatting, setIsChatting] = useState(false);

  const handleSaveIdea = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save ideas",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter an idea title",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const { data: newIdea, error } = await supabase
        .from("ideas")
        .insert({
          user_id: user.id,
          title: title.trim(),
          tagline: title.trim(),
          description: description.trim() || title.trim(),
          summary: description.trim() || title.trim(),
          problem: description.trim() || "",
          solution: "",
          niche: "General",
          icp: user.email || "",
          tags: ["validated"],
          pricing_model: null,
          opportunity_score: null,
          market_size_usd: null,
          is_public: true, // 🔑 REQUIRED
        })
        .select()
        .single();

      if (error) throw error;

      if (newIdea) {
        await supabase.functions.invoke("generate-idea-report", {
          body: {
            ideaId: newIdea.id,
            title: newIdea.title,
            description: newIdea.description,
            niche: newIdea.niche,
            tags: newIdea.tags ?? [],
          },
        });
      }

      toast({
        title: "Idea saved",
        description: "Your idea is now public and visible",
      });

      setTitle("");
      setDescription("");
      setChatMessages([]);
    } catch (err: any) {
      toast({
        title: "Save failed",
        description: err.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    setChatMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsChatting(true);

    const aiResponse = `Let’s pressure test this idea.

• Validate demand with real users
• Reduce scope to 1 killer feature
• Ship fast and iterate

Want me to stress-test market size or monetization?`;

    setTimeout(() => {
      setChatMessages((prev) => [...prev, { role: "assistant", content: aiResponse }]);
      setIsChatting(false);
    }, 800);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Research My Idea
          </CardTitle>
          <CardDescription>Describe your idea and save it publicly</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Idea title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea
            rows={6}
            placeholder="Describe the problem and solution"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Button onClick={handleSaveIdea} disabled={isSaving || !title.trim()} className="w-full">
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving…" : "Save Idea"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            AI Feedback
          </CardTitle>
          <CardDescription>Stress test your idea</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScrollArea className="h-[280px] border rounded p-4">
            {chatMessages.length === 0 ? (
              <div className="text-sm text-muted-foreground">Ask about validation, pricing, or risk.</div>
            ) : (
              chatMessages.map((m, i) => (
                <div key={i} className="mb-3">
                  <strong>{m.role === "user" ? "You" : "AI"}:</strong>
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                </div>
              ))
            )}
          </ScrollArea>
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask something"
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
            />
            <Button onClick={handleSendMessage} size="icon">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
